import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Clock, CheckCircle2, AlertCircle, ChevronRight, Loader2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

export default function BatchHistory() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/batches");
      setBatches(data);
    } catch (e) {
      toast.error("Gagal memuat batch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Hapus batch ini beserta semua exam yang dibuat?")) return;
    try {
      await api.delete(`/batches/${id}?delete_exams=true`);
      toast.success("Batch dihapus");
      setBatches((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      toast.error("Gagal hapus");
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto">
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
            Worklist
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
            Riwayat Batch
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            {batches.length} batch upload tersimpan
          </p>
        </div>
        <Link
          to="/batch"
          data-testid="link-new-batch"
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 text-sm font-medium shadow-lg shadow-indigo-500/20"
        >
          <Layers className="w-4 h-4" /> Batch Baru
        </Link>
      </div>

      <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 overflow-hidden">
        {loading && (
          <div className="p-10 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Memuat...
          </div>
        )}
        {!loading && batches.length === 0 && (
          <div className="p-16 text-center">
            <Layers className="w-8 h-8 mx-auto text-zinc-600 mb-3" />
            <div className="text-sm text-zinc-300 font-medium">Belum ada batch</div>
            <div className="text-xs text-zinc-500 mt-1">
              Upload beberapa X-ray sekaligus lewat halaman{" "}
              <Link to="/batch" className="text-indigo-400 underline">
                Batch Upload
              </Link>
              .
            </div>
          </div>
        )}
        {!loading && batches.length > 0 && (
          <div className="divide-y divide-zinc-900">
            <div className="hidden md:grid md:grid-cols-[1fr_120px_100px_100px_140px_60px] gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              <div>Batch</div>
              <div>Status</div>
              <div>Done</div>
              <div>Failed</div>
              <div>Created</div>
              <div />
            </div>
            {batches.map((b) => (
              <div
                key={b.id}
                data-testid={`batch-row-${b.id}`}
                className="grid grid-cols-1 md:grid-cols-[1fr_120px_100px_100px_140px_60px] gap-3 px-5 py-4 items-center"
              >
                <Link to={`/batches/${b.id}`} className="min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">
                    {b.notes || `Batch ${b.id.slice(0, 8)}`}
                  </div>
                  <div className="text-xs font-mono text-zinc-500 truncate">
                    {b.exam_date || "-"} · {b.total} file
                  </div>
                </Link>
                <StatusBadge status={b.status} />
                <div className="text-xs font-mono text-emerald-400">{b.done}</div>
                <div className="text-xs font-mono text-rose-400">{b.failed}</div>
                <div className="text-xs text-zinc-400">
                  {new Date(b.created_at).toLocaleString()}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => remove(b.id)}
                    data-testid={`delete-batch-${b.id}`}
                    className="p-1.5 rounded hover:bg-rose-500/10 text-zinc-500 hover:text-rose-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    processing: { text: "Processing", cls: "bg-indigo-500/10 text-indigo-300", icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    done: { text: "Done", cls: "bg-emerald-500/10 text-emerald-400", icon: <CheckCircle2 className="w-3 h-3" /> },
    failed: { text: "Failed", cls: "bg-rose-500/10 text-rose-400", icon: <AlertCircle className="w-3 h-3" /> },
    pending: { text: "Pending", cls: "bg-zinc-800 text-zinc-400", icon: <Clock className="w-3 h-3" /> },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded ${s.cls}`}>
      {s.icon}
      {s.text}
    </span>
  );
}
